import dayjs from 'dayjs';
import React from 'react';
import temperatureIcon from '../../../components/icons/temperatureIcon.svg';
import { regionSupportsTemperatureControl } from '../../../const/HubConfig';
import {
  InboundListOrder,
  OrderPackageSize,
  Region,
} from '../../../types/orderTypes';
import './OrderTable.scss';

function formatInterval(order: InboundListOrder): string {
  return `${dayjs(order.deliveryInterval.start).format('HH:mm')}-${dayjs(
    order.deliveryInterval.end
  ).format('HH:mm')} (${dayjs(order.deliveryInterval.start).format('DD MMM')})`;
}

interface ParcelAccumulator {
  totalCount: number;
  totalWeight: number;
  totalVolume: number;
}

function accumulateParcels(orders: InboundListOrder[]): ParcelAccumulator {
  if (orders.length > 0) {
    return orders
      .map(order => {
        const acc: ParcelAccumulator = {
          totalCount: order.packagesNumber,
          totalWeight: order.weightGrams,
          totalVolume: order.volumeCm3,
        };
        return acc;
      })
      .reduce((previous, current) => {
        return {
          totalCount: previous.totalCount + current.totalCount,
          totalWeight: previous.totalWeight + current.totalWeight,
          totalVolume: previous.totalVolume + current.totalVolume,
        };
      });
  } else {
    return {
      totalCount: 0,
      totalWeight: 0,
      totalVolume: 0,
    };
  }
}

function packageSize(order: InboundListOrder): string {
  if (order.xxl) {
    return 'XL';
  }

  const type = order.packageSize;

  switch (type) {
    case OrderPackageSize.Small:
      return 'Liten';
    case OrderPackageSize.Medium:
      return 'Medium';
    case OrderPackageSize.Large:
      return 'Stor';
    case OrderPackageSize.Xxl:
      return 'Ekstra Stor';
    default:
      return 'Ukjent';
  }
}

export function OrderTable({
  orders,
  region,
}: {
  orders: InboundListOrder[];
  region?: Region;
}): JSX.Element {
  const accumulatedParcels = accumulateParcels(orders);
  const showTemperatureControl = region
    ? regionSupportsTemperatureControl(region)
    : false;

  return (
    <div>
      <table className="data-table">
        <thead>
          <tr>
            <th data-sort="id" data-sortmethod="number">
              Ordre
            </th>
            <th data-sort="packagesNumber" data-sortmethod="number">
              Kolli
            </th>
            <th data-sort="packagesNumber" data-sortmethod="number">
              Vekt (kg)
            </th>
            <th data-sort="packagesNumber" data-sortmethod="number">
              Volum (liter)
            </th>
            <th data-sort="packagesNumber" data-sortmethod="number">
              Størrelse
            </th>

            <th data-sort="recipientAddress" data-sortmethod="string">
              Adresse (mottaker)
            </th>
            <th data-sort="recipientName" data-sortmethod="string">
              Navn (mottaker)
            </th>
            <th data-sort="deliverFrom" data-sortmethod="date">
              Leveringstid
            </th>
          </tr>
        </thead>

        <tbody>
          {orders.map(order => (
            <tr key={order.orderId} data-order-id={order.orderId}>
              <td className="is-number">{order.orderId}</td>
              <td className="is-number">
                {showTemperatureControl && order.temperatureControlled ? (
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    {order.packagesNumber}

                    <img
                      src={temperatureIcon}
                      style={{ height: 14, marginLeft: 8 }}
                      alt="Temperature control"
                    />
                  </span>
                ) : (
                  order.packagesNumber
                )}
              </td>
              <td className="is-number">{order.weightGrams / 1000}</td>
              <td className="is-number">{order.volumeCm3 / 1000}</td>
              <td className="is-number">{packageSize(order)}</td>
              <td>{order.recipientAddress}</td>
              <td>{order.recipientName}</td>
              <td className="is-number  is-nowrap">{formatInterval(order)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ height: 10 }} />
      <div className="order-table-summary">
        <div className="order-table-summary-row">
          <p>Antall ordre: {orders.length}</p>
          <p>Antall koli: {accumulatedParcels.totalCount}</p>
        </div>
        <div className="order-table-summary-row">
          <p> Total vekt: {accumulatedParcels.totalWeight / 1000} kg</p>
          <p> Total volum: {accumulatedParcels.totalVolume / 1000} liter</p>
        </div>
      </div>
    </div>
  );
}
